import { type Ref } from 'vue';
import { noop } from 'lodash-es';
import { v4 as uuid } from 'uuid';
import type { Passenger, PassengerKey } from '@aviata/booking-data';

import { formPassengerModel } from '@/entities/form-passenger';

import { useUserStore } from '@/shared/model';

import type { DocScanData } from '../api/doc-scan';
import {
  convertPassengerToPayload,
  localPassengersStorage,
  mapScanDataToPassenger,
} from '../lib';
import type { PassengerWithDocType } from '../types';

/**
 * Owns the doc-scan autofill plumbing — both the "OCR finished with a
 * result" path and the "let me edit before saving" path. The component
 * passes in:
 *   - `activePassengerKey` so we know when to no-op the flow,
 *   - `refreshLocalPassengers` to repopulate the visible list after we
 *     touch sessionStorage,
 *   - `onPassengerSelected` to route the autofilled passenger through
 *     the same logic as picking a saved one (FunSun guard, sheet close,
 *     emit) — the component owns that handler,
 *   - `onFormOpenWithPrefill` to open the manual form sheet (no scan
 *     result or "edit before save").
 *
 * After the local upsert + UI hand-off, the composable also fires a
 * best-effort backend create so the passenger ends up persisted to the
 * user's account; on success the temp uuid is rebound to the real id.
 * Errors from the mutation are swallowed (handled inside the mutation /
 * API layer) — we keep the temp-id local row either way.
 */
export const useDocScanFlow = (options: {
  activePassengerKey: Ref<PassengerKey | null>;
  refreshLocalPassengers: () => void;
  onPassengerSelected: (passenger: Passenger) => void;
  onFormOpenWithPrefill: (prefilledPassenger?: Passenger) => void;
}) => {
  const userStore = useUserStore();
  const createPassengerMutation = formPassengerModel.useCreatePassenger();

  const handleDocumentAutofillComplete = async (data?: DocScanData) => {
    if (!data || !options.activePassengerKey.value) {
      options.onFormOpenWithPrefill();
      return;
    }

    const passengerWithTempId: PassengerWithDocType = {
      ...mapScanDataToPassenger(data),
      id: uuid(),
    };

    localPassengersStorage.upsert(passengerWithTempId);
    options.refreshLocalPassengers();
    options.onPassengerSelected(passengerWithTempId);

    const userId = await userStore.getUserId();
    if (!userId) {
      return;
    }

    createPassengerMutation
      .createPassengerWithCallback(
        {
          userId,
          passenger: convertPassengerToPayload({
            passenger: passengerWithTempId,
          }),
        },
        createdPassenger => {
          const passengerWithBackendId: PassengerWithDocType = {
            ...passengerWithTempId,
            id: createdPassenger.id,
          };
          localPassengersStorage.upsert(passengerWithBackendId);
          options.refreshLocalPassengers();
        },
      )
      .catch(noop);
  };

  const handleDocumentAutofillEdit = (data: DocScanData) => {
    options.onFormOpenWithPrefill(mapScanDataToPassenger(data));
  };

  return {
    handleDocumentAutofillComplete,
    handleDocumentAutofillEdit,
  };
};
